import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon, ExitIcon, PersonIcon, IdCardIcon, BarChartIcon, LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';

const AdminPanel = ({ onLogout, backendUrl, onBack }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [users, setUsers] = useState([]);
  const [securityStats, setSecurityStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getAdminHeaders = useCallback(() => {
    const token = sessionStorage.getItem('adminToken');
    return {
      'x-admin-token': token
    };
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${backendUrl}/api/admin/dashboard`, {
        headers: getAdminHeaders()
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        onLogout();
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl, onLogout, getAdminHeaders]);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${backendUrl}/api/admin/purchases`, {
        headers: getAdminHeaders()
      });
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        onLogout();
      } else {
        setError('Failed to load purchase history');
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl, onLogout, getAdminHeaders]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${backendUrl}/api/admin/users`, {
        headers: getAdminHeaders()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        onLogout();
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl, onLogout, getAdminHeaders]);

  const fetchSecurityStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${backendUrl}/api/admin/security-stats`, {
        headers: getAdminHeaders()
      });
      setSecurityStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching security stats:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        onLogout();
      } else {
        setError('Failed to load security statistics');
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl, onLogout, getAdminHeaders]);

  useEffect(() => {
    switch (activeTab) {
      case 'dashboard':
        fetchDashboardData();
        break;
      case 'purchases':
        fetchPurchases();
        break;
      case 'users':
        fetchUsers();
        break;
      case 'security':
        fetchSecurityStats();
        break;
      default:
        break;
    }
  }, [activeTab, fetchDashboardData, fetchPurchases, fetchUsers, fetchSecurityStats]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const renderDashboard = () => (
    <div className="admin-dashboard">
      {loading ? (
        <div className="admin-loading">Loading dashboard...</div>
      ) : dashboardData ? (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon users-icon">
                <PersonIcon width={24} height={24} />
              </div>
              <div className="stat-info">
                <h3>Total Users</h3>
                <p className="stat-value">{dashboardData.totalUsers || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon credits-icon">
                <BarChartIcon width={24} height={24} />
              </div>
              <div className="stat-info">
                <h3>Total Credits Used</h3>
                <p className="stat-value">{dashboardData.totalCreditsUsed || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon revenue-icon">
                <IdCardIcon width={24} height={24} />
              </div>
              <div className="stat-info">
                <h3>Total Revenue</h3>
                <p className="stat-value">{formatCurrency(dashboardData.totalRevenue || 0)}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon images-icon">
                <BarChartIcon width={24} height={24} />
              </div>
              <div className="stat-info">
                <h3>Images Generated</h3>
                <p className="stat-value">{dashboardData.totalImages || 0}</p>
              </div>
            </div>
          </div>

          <div className="dashboard-section">
            <h3>Quick Stats</h3>
            <div className="quick-stats">
              <div className="quick-stat">
                <span className="label">Active Users (30 days)</span>
                <span className="value">{dashboardData.activeUsers || 0}</span>
              </div>
              <div className="quick-stat">
                <span className="label">New Users (7 days)</span>
                <span className="value">{dashboardData.newUsers || 0}</span>
              </div>
              <div className="quick-stat">
                <span className="label">Purchases (30 days)</span>
                <span className="value">{dashboardData.recentPurchases || 0}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <p>No dashboard data available</p>
      )}
    </div>
  );

  const renderPurchases = () => (
    <div className="admin-purchases">
      {loading ? (
        <div className="admin-loading">Loading purchases...</div>
      ) : purchases.length > 0 ? (
        <div className="table-container">
          <table className="admin-table purchases-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User ID</th>
                <th>Credits</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((purchase, index) => (
                <tr key={purchase.id || index}>
                  <td>{formatDate(purchase.created)}</td>
                  <td className="user-id">{purchase.userId || 'N/A'}</td>
                  <td>{purchase.credits}</td>
                  <td>{formatCurrency(purchase.amount / 100)}</td>
                  <td>
                    <span className={`status-badge status-${purchase.status}`}>
                      {purchase.status}
                    </span>
                  </td>
                  <td>
                    {purchase.receiptUrl ? (
                      <a
                        href={purchase.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="receipt-link"
                      >
                        View Receipt
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-data">No purchase records found</p>
      )}
    </div>
  );

  const renderUsers = () => (
    <div className="admin-users">
      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : users.length > 0 ? (
        <div className="table-container">
          <table className="admin-table users-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Credits</th>
                <th>Credits Used</th>
                <th>Images</th>
                <th>Created</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id || index}>
                  <td className="user-id">{user.id}</td>
                  <td>{user.credits || 0}</td>
                  <td>{user.totalCreditsUsed || 0}</td>
                  <td>{user.totalImages || 0}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.lastActivity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-data">No users found</p>
      )}
    </div>
  );

  const renderSecurity = () => (
    <div className="admin-security">
      {loading ? (
        <div className="admin-loading">Loading security stats...</div>
      ) : securityStats ? (
        <>
          <div className="security-stats-grid">
            <div className="security-stat-card">
              <h4>Total Alerts</h4>
              <p className="security-value">{securityStats.totalAlerts || 0}</p>
            </div>
            <div className="security-stat-card warning">
              <h4>Flagged Users</h4>
              <p className="security-value">{securityStats.flaggedUsers || 0}</p>
            </div>
            <div className="security-stat-card">
              <h4>Large Purchases</h4>
              <p className="security-value">{securityStats.largePurchases || 0}</p>
            </div>
            <div className="security-stat-card danger">
              <h4>Suspicious Activity</h4>
              <p className="security-value">{securityStats.suspiciousActivity || 0}</p>
            </div>
          </div>

          {securityStats.recentAlerts && securityStats.recentAlerts.length > 0 && (
            <div className="recent-alerts">
              <h3>Recent Security Alerts</h3>
              <div className="alerts-list">
                {securityStats.recentAlerts.map((alert, index) => (
                  <div key={index} className={`alert-item alert-${alert.severity}`}>
                    <div className="alert-header">
                      <span className="alert-type">{alert.type}</span>
                      <span className="alert-date">{formatDate(alert.timestamp)}</span>
                    </div>
                    <p className="alert-message">{alert.message}</p>
                    <span className="alert-user">User: {alert.userId}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="no-data">No security statistics available</p>
      )}
    </div>
  );

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="btn btn-secondary back-btn-admin" onClick={onBack}>
            <ArrowLeftIcon style={{ marginRight: '8px' }} />
            Back to App
          </button>
          <h1>Admin Panel</h1>
        </div>
        <button className="btn btn-secondary logout-btn" onClick={onLogout}>
          <ExitIcon style={{ marginRight: '8px' }} />
          Logout
        </button>
      </header>

      <nav className="admin-nav">
        <button
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <BarChartIcon style={{ marginRight: '8px' }} />
          Dashboard
        </button>
        <button
          className={`nav-tab ${activeTab === 'purchases' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          <IdCardIcon style={{ marginRight: '8px' }} />
          Purchases
        </button>
        <button
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <PersonIcon style={{ marginRight: '8px' }} />
          Users
        </button>
        <button
          className={`nav-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <LockClosedIcon style={{ marginRight: '8px' }} />
          Security
        </button>
      </nav>

      <main className="admin-content">
        {error && <div className="admin-error">{error}</div>}

        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'purchases' && renderPurchases()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'security' && renderSecurity()}
      </main>
    </div>
  );
};

export default AdminPanel;
